import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useQuery } from "@tanstack/react-query";
import { geoArea, geoGraticule10, geoNaturalEarth1, geoPath } from "d3-geo";
import { select } from "d3-selection";
import {
  type D3ZoomEvent,
  type ZoomBehavior,
  type ZoomTransform,
  zoom as d3Zoom,
  zoomIdentity,
} from "d3-zoom";
import type { Feature, FeatureCollection, Geometry } from "geojson";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { feature } from "topojson-client";
import type { Topology } from "topojson-specification";

import { cn } from "@/lib/utils";

import {
  type CountryDomination,
  type CuratorStat,
  fitLeaderRadius,
  layoutCountryBubbles,
  leaderRadiusForArea,
  toMainlandFeature,
} from "./lib";

const ATLAS_URL = "/world-110m-countries.json";
const ANTARCTICA_ID = "010";
const MAX_ZOOM = 4; // country level — deliberately not street level
const FIT_PADDING = 28;

type CountryFeature = Feature<Geometry, { name?: string }>;

interface Bubble {
  anchor: [number, number]; // base (untransformed) country centroid
  dx: number; // screen-space offset from the anchor
  dy: number;
  r: number;
  isLeader: boolean;
  curator: CuratorStat;
  countryName: string;
}

interface TipState {
  x: number;
  y: number;
  title: string;
  rows: Array<{ label: string; value: string; color?: string }>;
}

const FALLBACK_COLOR = "var(--chart-2)";

function useContainerWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      setWidth(Math.floor(entries[0]?.contentRect.width ?? 0));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

interface WorldDominationMapProps {
  countries: CountryDomination[];
  youUid: string | null;
  colorByUid: Map<string, string>;
  height?: number;
}

export const WorldDominationMap = ({
  countries,
  youUid,
  colorByUid,
  height = 300,
}: WorldDominationMapProps) => {
  const [wrapRef, width] = useContainerWidth();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [transform, setTransform] = useState<ZoomTransform>(zoomIdentity);
  const [tip, setTip] = useState<TipState | null>(null);

  const atlasQ = useQuery({
    queryKey: ["world-atlas-110m"],
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async (): Promise<Topology> => {
      const res = await fetch(ATLAS_URL);
      if (!res.ok) throw new Error(`world atlas fetch failed: ${res.status}`);
      return (await res.json()) as Topology;
    },
  });

  const byNumericId = useMemo(
    () => new Map(countries.map((c) => [c.numericId, c])),
    [countries],
  );
  const maxCountryTotal = useMemo(
    () => Math.max(1, ...countries.map((c) => c.total)),
    [countries],
  );

  // Projection, country path strings, bubble base positions, zoom limits —
  // all in base (k=1) coordinates; the zoom transform is applied on top.
  const geo = useMemo(() => {
    if (!atlasQ.data || width <= 0) return null;
    const fc = feature(
      atlasQ.data,
      atlasQ.data.objects.countries,
    ) as unknown as FeatureCollection<Geometry, { name?: string }>;
    const sphericalArea = (g: Geometry) =>
      geoArea(g as Parameters<typeof geoArea>[0]);
    // Keep only each country's main landmass — far-flung territories (e.g.
    // French Guiana for FR) otherwise render in South America, stretch the
    // world-fit projection, and pull markers out of the country.
    const features = fc.features
      .filter((f) => String(f.id) !== ANTARCTICA_ID)
      .map((f) => toMainlandFeature(f, sphericalArea) as CountryFeature);
    const active = features.filter((f) =>
      byNumericId.has(String(f.id).padStart(3, "0")),
    );

    const sphere = { type: "Sphere" } as const;
    const projection = geoNaturalEarth1();
    if (active.length > 0) {
      projection.fitExtent(
        [
          [FIT_PADDING, FIT_PADDING],
          [width - FIT_PADDING, height - FIT_PADDING],
        ],
        { type: "FeatureCollection", features: active },
      );
    } else {
      projection.fitSize([width, height], sphere);
    }
    const path = geoPath(projection);
    const worldScale = geoNaturalEarth1()
      .fitSize([width, height], sphere)
      .scale();
    const minZoom = Math.min(1, worldScale / projection.scale());
    const sphereBounds = path.bounds(sphere);

    const countryShapes = features.map((f) => {
      const [[x0, y0], [x1, y1]] = path.bounds(f);
      return {
        id: String(f.id).padStart(3, "0"),
        name: f.properties?.name ?? "Unknown",
        d: path(f) ?? "",
        centroid: path.centroid(f) as [number, number],
        area: Math.abs(path.area(f)), // projected px², for sizing markers
        minExtent: Math.min(x1 - x0, y1 - y0), // smaller projected side, px
      };
    });

    const bubbles: Bubble[] = [];
    for (const shape of countryShapes) {
      const domination = byNumericId.get(shape.id);
      if (!domination) continue;
      // One bubble per country (its top curator), capped to the country's own
      // size so it never spills past the border — even when zoomed out.
      const placements = layoutCountryBubbles(
        domination.curators,
        fitLeaderRadius(
          leaderRadiusForArea(shape.area),
          shape.area,
          shape.minExtent,
        ),
      );
      for (const placement of placements) {
        bubbles.push({
          ...placement,
          anchor: shape.centroid,
          countryName: domination.countryName,
        });
      }
    }

    return {
      graticule: path(geoGraticule10()) ?? "",
      countryShapes,
      bubbles,
      minZoom,
      sphereBounds,
    };
  }, [atlasQ.data, width, height, byNumericId]);

  // Attach (or re-attach) the zoom behavior whenever geometry is rebuilt.
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || !geo) return;
    const [[bx0, by0], [bx1, by1]] = geo.sphereBounds;
    const behavior = d3Zoom<SVGSVGElement, unknown>()
      .scaleExtent([geo.minZoom, MAX_ZOOM])
      .translateExtent([
        [bx0 - 40, by0 - 40],
        [bx1 + 40, by1 + 40],
      ])
      .on("zoom", (ev: D3ZoomEvent<SVGSVGElement, unknown>) => {
        setTransform(ev.transform);
        setTip(null);
      });
    zoomRef.current = behavior;
    select(svg).call(behavior);
    setTransform(zoomIdentity);
    select(svg).call(behavior.transform, zoomIdentity);
    return () => {
      select(svg).on(".zoom", null);
    };
  }, [geo]);

  const zoomBy = (factor: number) => {
    const svg = svgRef.current;
    if (!svg || !zoomRef.current) return;
    zoomRef.current.scaleBy(select(svg), factor);
  };
  const resetZoom = () => {
    const svg = svgRef.current;
    if (!svg || !zoomRef.current) return;
    zoomRef.current.transform(select(svg), zoomIdentity);
  };

  const moveTip = (
    e: React.MouseEvent,
    title: string,
    rows: TipState["rows"],
  ) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTip({
      x: Math.min(e.clientX - rect.left + 14, Math.max(0, width - 170)),
      y: Math.max(e.clientY - rect.top - 10, 4),
      title,
      rows,
    });
  };

  if (atlasQ.isError) {
    return (
      <div
        className="flex items-center justify-center text-xs text-muted-foreground"
        style={{ height }}
      >
        Could not load the world map.
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative overflow-hidden" style={{ height }}>
      {/* zoom controls */}
      <div className="absolute right-2 top-2 z-10 flex flex-col gap-1">
        {(
          [
            [
              "Zoom in",
              <Plus key="i" className="h-3.5 w-3.5" />,
              () => zoomBy(1.4),
            ],
            [
              "Zoom out",
              <Minus key="o" className="h-3.5 w-3.5" />,
              () => zoomBy(1 / 1.4),
            ],
            [
              "Reset view",
              <RotateCcw key="r" className="h-3 w-3" />,
              resetZoom,
            ],
          ] as const
        ).map(([label, icon, onClick]) => (
          <button
            key={label}
            type="button"
            title={label}
            aria-label={label}
            onClick={onClick}
            className="flex h-7 w-7 items-center justify-center rounded-md border bg-card/90 text-foreground hover:bg-muted"
          >
            {icon}
          </button>
        ))}
      </div>

      {geo && (
        <svg
          ref={svgRef}
          viewBox={`0 0 ${width} ${height}`}
          className="block h-full w-full cursor-grab active:cursor-grabbing"
        >
          {/* map layer — zoom transform applies */}
          <g transform={transform.toString()}>
            <path
              d={geo.graticule}
              fill="none"
              stroke="currentColor"
              strokeOpacity={0.05}
              vectorEffect="non-scaling-stroke"
            />
            {geo.countryShapes.map((shape) => {
              const domination = byNumericId.get(shape.id);
              // The country is tinted with the colour of its dominant curator
              // (curators are sorted by count desc, so [0] leads it).
              const topColor = domination
                ? (colorByUid.get(domination.curators[0]?.uid) ??
                  FALLBACK_COLOR)
                : "var(--muted)";
              return (
                <path
                  key={shape.id + shape.name}
                  d={shape.d}
                  fill={topColor}
                  fillOpacity={
                    domination
                      ? 0.35 + 0.45 * (domination.total / maxCountryTotal)
                      : 0.6
                  }
                  stroke="var(--background)"
                  strokeWidth={0.6}
                  vectorEffect="non-scaling-stroke"
                  onMouseMove={(e) => {
                    if (!domination) return;
                    moveTip(
                      e,
                      `${domination.countryName} · ${domination.total} resolves`,
                      domination.curators.map((c) => ({
                        label: c.displayName,
                        value: String(c.count),
                        color: colorByUid.get(c.uid) ?? FALLBACK_COLOR,
                      })),
                    );
                  }}
                  onMouseLeave={() => setTip(null)}
                />
              );
            })}
          </g>

          {/* bubble layer — screen space. Each marker's base radius is already
              capped to its country's size (see fitLeaderRadius). It then holds a
              constant size when zoomed in, and shrinks with the map when zooming
              out (s = min(1, k)), so it never grows larger than its country at
              any zoom. */}
          {geo.bubbles.map((b) => {
            const s = Math.min(1, transform.k);
            const r = b.r * s;
            const [ax, ay] = transform.apply(b.anchor);
            const cx = ax + b.dx * s;
            const cy = ay + b.dy * s;
            const isYou = youUid != null && b.curator.uid === youUid;
            const color = colorByUid.get(b.curator.uid) ?? FALLBACK_COLOR;
            const clipId = `wd-clip-${b.curator.uid}-${b.countryName.replace(/\W/g, "")}`;
            return (
              // pointer-events-none so hovering the bubble falls through to the
              // country path beneath it — only the country tooltip shows, never a
              // per-bubble one.
              <g
                key={clipId}
                transform={`translate(${cx},${cy})`}
                className="pointer-events-none"
              >
                <circle
                  r={r + 2.5 * s}
                  fill="var(--card)"
                  stroke={color}
                  strokeWidth={(isYou ? 3 : 1.75) * s}
                />
                <circle r={r} fill={color} fillOpacity={0.9} />
                <text
                  textAnchor="middle"
                  dy="0.35em"
                  fill="var(--foreground)"
                  fontWeight={600}
                  fontSize={r * 0.8}
                >
                  {b.curator.displayName.charAt(0).toUpperCase()}
                </text>
                {b.curator.photoUrl && (
                  <>
                    <clipPath id={clipId}>
                      <circle r={r} />
                    </clipPath>
                    <image
                      href={b.curator.photoUrl}
                      x={-r}
                      y={-r}
                      width={r * 2}
                      height={r * 2}
                      clipPath={`url(#${clipId})`}
                      preserveAspectRatio="xMidYMid slice"
                    />
                  </>
                )}
                {/* Count sits on the marker's exposed side: bottom-right for
                    the leader (always on top), bottom-left for runners-up
                    (their bottom-left peeks out from under the leader). */}
                <text
                  x={b.isLeader ? r * 0.7 : -r * 0.7}
                  y={r * 0.95}
                  textAnchor={b.isLeader ? "start" : "end"}
                  fill="var(--foreground)"
                  fontSize={r * 0.6}
                  fontWeight={600}
                  paintOrder="stroke"
                  stroke="var(--background)"
                  strokeWidth={3 * s}
                >
                  {b.curator.count}
                </text>
              </g>
            );
          })}
        </svg>
      )}

      {tip && (
        <div
          className={cn(
            "pointer-events-none absolute z-20 min-w-[130px] rounded-md border",
            "bg-popover px-2.5 py-2 text-[11px] text-popover-foreground shadow-md",
          )}
          style={{ left: tip.x, top: tip.y }}
        >
          <div className="text-xs font-semibold">{tip.title}</div>
          {tip.rows.map((r) => (
            <div
              key={r.label}
              className="mt-0.5 flex items-center justify-between gap-3 text-muted-foreground"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {r.color && (
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: r.color }}
                  />
                )}
                <span className="truncate">{r.label}</span>
              </span>
              <span className="tabular-nums">{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
